import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { adminApi } from "../../api/admin";
import DashboardLoadingState from "../../components/dashboard/DashboardLoadingState";

const BRAND = { orange: "#F35A3A", dark: "var(--wp-panel-text-primary, #19242F)" };

const STATUS_OPTIONS = [
  { value: "", label: "Todos los estados" },
  { value: "connected", label: "Conectadas" },
  { value: "incomplete", label: "Incompletas" },
  { value: "error", label: "Con error" },
  { value: "disabled", label: "Deshabilitadas" },
];

const STATE_LABELS = {
  connected: "Conectada",
  incomplete: "Incompleta",
  error: "Con error",
  disabled: "Deshabilitada",
};

function formatDate(value) {
  if (!value) return "Sin registro";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Sin registro";
  return new Intl.DateTimeFormat("es-AR", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function tone(state) {
  if (state === "connected") return "success";
  if (state === "error") return "error";
  if (state === "incomplete") return "warning";
  return "default";
}

function accountLabel(account = {}) {
  return account.nickname || account.email || account.id || "Sin cuenta pública informada";
}

export default function SuperIntegrationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [qDraft, setQDraft] = React.useState(searchParams.get("q") || "");

  const paramsKey = searchParams.toString();
  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await adminApi.commandCenterIntegrations(Object.fromEntries(searchParams.entries()));
      setData(response.integrations);
    } catch (err) {
      setError(err.message || "No se pudieron cargar las integraciones");
    } finally {
      setLoading(false);
    }
  }, [paramsKey, searchParams]);

  React.useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <DashboardLoadingState label="Cargando integraciones…" minHeight={420} />;
  }

  const update = (key, value, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (resetPage) next.delete("page");
    setSearchParams(next);
  };

  const submitSearch = (event) => {
    event.preventDefault();
    update("q", qDraft);
  };

  const clear = () => {
    setQDraft("");
    setSearchParams(new URLSearchParams());
  };

  const filtersActive = ["q", "provider", "status"].some((key) => Boolean(searchParams.get(key)));

  return (
    <Stack spacing={2.3}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={1.2} alignItems={{ md: "flex-start" }}>
        <Box>
          <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate("/superadmin")} sx={{ p: 0, mb: .8, textTransform: "none", fontWeight: 850, color: BRAND.dark }}>
            Volver al Super Panel
          </Button>
          <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark, fontSize: { xs: "1.8rem", md: "2.6rem" } }}>Integraciones</Typography>
          <Typography sx={{ mt: .4, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", maxWidth: 760 }}>
            Estado global de conexiones por proveedor. Esta vista sólo usa datos públicos/operativos y nunca expone credenciales.
          </Typography>
        </Box>
        <Button startIcon={<RefreshRoundedIcon />} onClick={load} disabled={loading} variant="outlined" sx={{ textTransform: "none", fontWeight: 900 }}>Actualizar</Button>
      </Stack>

      <Card elevation={0} sx={{ border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", borderRadius: 3.5 }}>
        <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
          <Box component="form" onSubmit={submitSearch}>
            <Grid container spacing={1.2} alignItems="center">
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth
                  size="small"
                  label="Buscar proyecto o slug"
                  value={qDraft}
                  onChange={(event) => setQDraft(event.target.value)}
                  InputProps={{ endAdornment: <SearchRoundedIcon fontSize="small" /> }}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField fullWidth size="small" label="Proveedor" value={searchParams.get("provider") || ""} onChange={(event) => update("provider", event.target.value)} placeholder="mercadopago / modo" />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField fullWidth select size="small" label="Estado" value={searchParams.get("status") || ""} onChange={(event) => update("status", event.target.value)}>
                  {STATUS_OPTIONS.map((option) => <MenuItem key={option.value || "all"} value={option.value}>{option.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <Stack direction="row" spacing={1}>
                  <Button type="submit" variant="contained" sx={{ bgcolor: BRAND.orange, textTransform: "none", fontWeight: 900 }}>Buscar</Button>
                  {filtersActive ? <Button onClick={clear} sx={{ textTransform: "none", fontWeight: 850 }}>Limpiar</Button> : null}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      {error ? <Alert severity="error" action={<Button color="inherit" onClick={load}>Reintentar</Button>}>{error}</Alert> : null}
      {loading ? <DashboardLoadingState label="Actualizando integraciones…" minHeight={230} /> : null}
      {!loading && !error && data?.items?.length === 0 ? <Alert severity="info">No hay integraciones que coincidan con estos filtros.</Alert> : null}

      {!loading && data?.items?.length ? (
        <Grid container spacing={1.5}>
          {data.items.map((item) => (
            <Grid item xs={12} md={6} lg={4} key={item.id}>
              <Card elevation={0} sx={{ height: "100%", border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)", borderRadius: 3.2 }}>
                <CardActionArea
                  disabled={!item.project?.slug}
                  onClick={() => item.project?.slug && navigate(`/superadmin/proyectos/${item.project.slug}`)}
                  sx={{ height: "100%", textAlign: "left", alignItems: "stretch" }}
                >
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: BRAND.dark, overflowWrap: "anywhere" }}>{item.project?.name || "Proyecto no resoluble"}</Typography>
                        <Typography sx={{ mt: .15, fontSize: ".75rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{item.project?.slug ? `/${item.project.slug}` : "Sin slug"}</Typography>
                      </Box>
                      <Chip size="small" label={STATE_LABELS[item.state] || item.state} color={tone(item.state)} />
                    </Stack>

                    <Stack direction="row" spacing={.7} useFlexGap flexWrap="wrap" sx={{ mt: 1.25 }}>
                      <Chip size="small" variant="outlined" label={item.provider || "provider desconocido"} />
                      <Chip size="small" variant="outlined" label={item.mode || "modo desconocido"} />
                      <Chip size="small" variant="outlined" label={item.connectionStatus || "not_configured"} />
                    </Stack>

                    <Box sx={{ mt: 1.5, p: 1.2, borderRadius: 2.3, bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)" }}>
                      <Typography sx={{ fontSize: ".7rem", fontWeight: 850, color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Cuenta pública</Typography>
                      <Typography sx={{ mt: .2, fontSize: ".82rem", fontWeight: 850, color: BRAND.dark, overflowWrap: "anywhere" }}>{accountLabel(item.account)}</Typography>
                    </Box>

                    <Grid container spacing={1.2} sx={{ mt: .2 }}>
                      <Grid item xs={6}><Typography sx={{ fontSize: ".69rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Última prueba</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 800 }}>{formatDate(item.lastTestedAt)}</Typography></Grid>
                      <Grid item xs={6}><Typography sx={{ fontSize: ".69rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Última sync OK</Typography><Typography sx={{ fontSize: ".78rem", fontWeight: 800 }}>{formatDate(item.lastSuccessfulSyncAt)}</Typography></Grid>
                    </Grid>

                    {item.lastError ? <Alert severity="warning" sx={{ mt: 1.4 }}>{item.lastError}</Alert> : <Typography sx={{ mt: 1.4, fontSize: ".76rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>Sin error persistido.</Typography>}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : null}

      {!loading && data?.pages > 1 ? (
        <Stack direction="row" justifyContent="center">
          <Pagination count={data.pages} page={Number(data.page || 1)} onChange={(_event, page) => update("page", String(page), false)} color="primary" />
        </Stack>
      ) : null}

      {!loading && data ? <Typography sx={{ textAlign: "center", fontSize: ".78rem", color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{data.total} integración{data.total === 1 ? "" : "es"} en el resultado</Typography> : null}
    </Stack>
  );
}
